import axios from 'axios';
import * as SecureStore from 'expo-secure-store';
import ENV from '../config/environments';
import logger from '../utils/logger';

// Create API client with environment-based configuration
const api = axios.create({
  baseURL: ENV.API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: ENV.TIMEOUT,
});

// Intercept requests to add auth token
api.interceptors.request.use(
  async (config) => {
    // Log the API request
    logger.apiRequest(config.method.toUpperCase(), config.url, config.data);
    
    const token = await getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    logger.error('API request error', error);
    return Promise.reject(error);
  }
);

// Intercept responses to handle errors consistently
api.interceptors.response.use(
  (response) => {
    // Log the API response
    logger.apiResponse(
      response.config.method.toUpperCase(),
      response.config.url,
      response.data,
      response.status
    );
    return response.data;
  },
  (error) => {
    const enhancedError = handleApiError(error);
    logger.error('API response error', enhancedError);
    return Promise.reject(enhancedError);
  }
);

/**
 * Helper function to get the stored auth token
 * @returns {Promise<string|null>} The auth token or null
 */
const getAuthToken = async () => {
  try {
    // Check if we're in a web environment
    if (typeof window !== 'undefined' && !window.localStorage) {
      return await SecureStore.getItemAsync('userToken');
    } else {
      // Use localStorage for web environment
      return localStorage.getItem('userToken');
    }
  } catch (error) {
    logger.error('Error getting auth token', error);
    return null;
  }
};

/**
 * Login user with credentials
 * @param {string} username - The user's email
 * @param {string} password - The user's password
 * @returns {Promise<object>} The user data and token
 */
export const loginUser = async (username, password) => {
  try {
    return await api.post('/users/login', { username, password });
  } catch (error) {
    throw error;
  }
};

/**
 * Send password reset request
 * @param {string} email - The user's email
 * @returns {Promise<object>} The response data
 */
export const forgotPassword = async (email) => {
  try {
    return await api.post('/users/forgot-password', { email });
  } catch (error) {
    throw error;
  }
};

/**
 * Reset password with verification code
 * @param {string} email - The user's email
 * @param {string} resetCode - The verification code
 * @param {string} newPassword - The new password
 * @returns {Promise<object>} The response data
 */
export const resetPassword = async (email, resetCode, newPassword) => {
  try {
    return await api.post('/users/reset-password', {
      email,
      resetCode,
      newPassword,
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Get user profile data
 * @returns {Promise<object>} The user profile data
 */
export const getUserProfile = async () => {
  try {
    return await api.get('/users/profile');
  } catch (error) {
    throw error;
  }
};

/**
 * Get daily sales summary for the specified client
 * @param {number} clientId - The client ID
 * @returns {Promise<object>} The daily summary data
 */
export const getDailySummary = async (clientId) => {
  try {
    //console.log('🚀 API Call: getDailySummary for clientId:', clientId);
    const response = await api.get(`/reports/daily-summary/${clientId}`);
    //console.log('📡 API Response Status:', response.status);
    //console.log('📡 API Response Headers:', response.headers);
    //console.log('📡 API Response Data:', JSON.stringify(response, null, 2));
    return response;
  } catch (error) {
    console.error('❌ API Error in getDailySummary:', error);
    console.error('❌ Error response:', error.response?.data);
    console.error('❌ Error status:', error.response?.status);
    throw error;
  }
};

/**
 * Get sales by date range for the specified client
 * @param {number} clientId - The client ID
 * @param {string} startDate - The start date (YYYY-MM-DD)
 * @param {string} endDate - The end date (YYYY-MM-DD)
 * @returns {Promise<object>} The sales data for the date range
 */
export const getSalesByDateRange = async (clientId, startDate, endDate) => {
  try {
    return await api.get(`/reports/sales-by-date/${clientId}`, {
      params: { startDate, endDate },
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Get top selling products for the specified client
 * @param {number} clientId - The client ID
 * @param {string} startDate - The start date (YYYY-MM-DD)
 * @param {string} endDate - The end date (YYYY-MM-DD)
 * @param {number} limit - Maximum number of products to return
 * @returns {Promise<object>} The top products data
 */
export const getTopSellingProducts = async (clientId, startDate, endDate, limit = 10) => {
  try {
    return await api.get(`/reports/top-products/${clientId}`, {
      params: { startDate, endDate, limit },
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Send daily sales summary via SMS
 * @param {number} clientId - The client ID
 * @param {string} phoneNumber - The phone number to send SMS to (E.164 format)
 * @returns {Promise<object>} The result of the SMS operation
 */
export const sendDailySalesSummary = async (clientId, phoneNumber) => {
  try {
    return await api.post('/notifications/daily-summary-sms', {
      clientId,
      phoneNumber
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Subscribe to daily sales notifications
 * @param {number} clientId - The client ID
 * @param {string} phoneNumber - The phone number to send SMS to (E.164 format)
 * @param {string} notificationType - The type of notification (default: 'daily')
 * @returns {Promise<object>} The result of the subscription operation
 */
export const subscribeToNotifications = async (clientId, phoneNumber, notificationType = 'daily') => {
  try {
    return await api.post('/notifications/subscribe', {
      clientId,
      phoneNumber,
      notificationType
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Unsubscribe from notifications
 * @param {number} clientId - The client ID
 * @param {string} notificationType - The type of notification to unsubscribe from (optional)
 * @returns {Promise<object>} The result of the unsubscribe operation
 */
export const unsubscribeFromNotifications = async (clientId, notificationType) => {
  try {
    return await api.post('/notifications/unsubscribe', {
      clientId,
      notificationType
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Send order status notification to a customer
 * @param {number} clientId - The client ID
 * @param {string} invoiceNo - The invoice number
 * @param {string} status - The new status of the order (e.g., "confirmed", "shipped", "delivered")
 * @param {string} additionalInfo - Any additional information to include (optional)
 * @param {string} phoneNumber - Override phone number (optional, will use customer's number from order if not provided)
 * @returns {Promise<object>} The result of the notification operation
 */
export const sendOrderStatusNotification = async (clientId, invoiceNo, status, additionalInfo = '', phoneNumber = null) => {
  try {
    const payload = {
      clientId,
      invoiceNo,
      status,
      additionalInfo
    };
    
    if (phoneNumber) {
      payload.phoneNumber = phoneNumber;
    }
    
    return await api.post('/notifications/order-status', payload);
  } catch (error) {
    throw error;
  }
};

/**
 * Send batch order status notifications for a date
 * @param {number} clientId - The client ID
 * @param {string} status - The status to set for all orders (e.g., "shipped", "delivered")
 * @param {string} statusDate - The date of orders to update (YYYY-MM-DD)
 * @param {string} message - Additional message to include in notifications (optional)
 * @returns {Promise<object>} The result of the batch operation
 */
export const sendBatchOrderStatusNotifications = async (clientId, status, statusDate, message = '') => {
  try {
    return await api.post('/notifications/batch-order-status', {
      clientId,
      status,
      statusDate,
      message
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Send order receipt notification to a customer
 * @param {number} clientId - The client ID
 * @param {string} invoiceNo - The invoice number
 * @param {string} customMessage - Custom message to include in the receipt (optional)
 * @param {string} phoneNumber - Override phone number (optional, will use customer's number from order if not provided)
 * @returns {Promise<object>} The result of the notification operation
 */
export const sendOrderReceiptNotification = async (clientId, invoiceNo, customMessage = '', phoneNumber = null) => {
  try {
    const payload = {
      clientId,
      invoiceNo,
      customMessage
    };
    
    if (phoneNumber) {
      payload.phoneNumber = phoneNumber;
    }
    
    return await api.post('/notifications/order-receipt', payload);
  } catch (error) {
    throw error;
  }
};

/**
 * Handle API errors consistently
 * @param {Error} error - The error object from axios
 * @returns {Error} A standardized error object
 */
const handleApiError = (error) => {
  let errorMessage = 'Something went wrong. Please try again.';

  if (error.response) {
    // Server responded with an error status
    const { data, status } = error.response;
    
    if (status === 401) {
      errorMessage = 'Your session has expired. Please login again.';
    } else if (status === 403) {
      errorMessage = 'You are not authorized to access this resource.';
    } else if (status === 404) {
      errorMessage = 'The requested resource was not found.';
    } else if (status >= 500) {
      errorMessage = 'Server error. Please try again later.';
    } else if (data && data.message) {
      errorMessage = data.message;
    }
  } else if (error.request) {
    // Request was made but no response received
    errorMessage = 'Network error. Please check your connection and try again.';
  }

  const customError = new Error(errorMessage);
  customError.originalError = error;
  return customError;
};